import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { fetchMe, isLoggedIn, login as apiLogin, logout as apiLogout, setSessionExpiredHandler } from 'api/client';
import { clearAllExerciseDrafts } from 'utils/exerciseDrafts';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(isLoggedIn());
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    if (!isLoggedIn()) return;
    fetchMe()
      .then(setUser)
      .catch(() => apiLogout())
      .finally(() => setInitializing(false));
  }, []);

  // Until this existed, a session dying mid-work left the app rendering
  // normally while every request failed silently. Dropping the user here
  // makes AuthGuard redirect, and the flag lets the login page explain why
  // they landed back there instead of appearing to have logged them out at
  // random.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      setSessionExpired(true);
      setUser(null);
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  const login = useCallback(async (email, password) => {
    await apiLogin(email, password);
    const me = await fetchMe();
    setSessionExpired(false);
    setUser(me);
    return me;
  }, []);

  const logout = useCallback(() => {
    apiLogout();
    // Deliberate sign-out, unlike an expired session: the student is done, so
    // don't leave their coursework readable by the next person on this machine.
    clearAllExerciseDrafts();
    setSessionExpired(false);
    setUser(null);
  }, []);

  return <AuthContext.Provider value={{ user, initializing, sessionExpired, login, logout }}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = { children: PropTypes.node };

export default function useAuth() {
  return useContext(AuthContext);
}
